import { cn } from '@/lib/utils'

/** Overlap into the corner arc so the vertical stroke meets without a subpixel gap. */
const VERTICAL_JUNCTION_OVERLAP_PX = 1

type VerticalBorderLineProps = {
  borderName: string
  side: 'left' | 'right'
  topOffset: number
  /** Clearance for a bottom corner box — shortens the line from below. */
  bottomOffset?: number
  /** Animation sweep along the line. Defaults to top → bottom. */
  drawDirection?: 'ttb' | 'btt'
  lineClassName?: string
  inView: boolean
  animateDraw?: boolean
  drawDelayMs?: number
  drawDurationMs?: number
}

/** Vertical column divider — scaleY draw between top and bottom corner offsets. */
export function VerticalBorderLine({
  borderName,
  side,
  topOffset,
  bottomOffset = 0,
  drawDirection = 'ttb',
  lineClassName = 'bg-white',
  inView,
  animateDraw = false,
  drawDelayMs = 0,
  drawDurationMs = 400,
}: VerticalBorderLineProps) {
  const drawn = !animateDraw || inView
  const overlap = VERTICAL_JUNCTION_OVERLAP_PX
  const top = Math.max(0, topOffset - overlap)
  const bottomInset =
    bottomOffset > 0 ? Math.max(0, bottomOffset - overlap) : 0

  return (
    <div
      aria-hidden
      data-border-name={borderName}
      className={cn(
        'pointer-events-none absolute z-10 w-px',
        drawDirection === 'btt' ? 'origin-bottom' : 'origin-top',
        side === 'right' ? 'right-0' : 'left-0',
        lineClassName,
        animateDraw && 'transition-transform ease-in-out',
        drawn ? 'scale-y-100' : 'scale-y-0',
      )}
      style={{
        top,
        height: `calc(100% - ${top}px - ${bottomInset}px)`,
        ...(animateDraw
          ? {
              transitionDuration: `${drawDurationMs}ms`,
              transitionDelay: `${drawDelayMs}ms`,
            }
          : undefined),
      }}
    />
  )
}
